<script>
import { Log } from '@artup/utilities'
import { Account, Countries, StandardSecurityProfiles } from '@artup/model'
import { FormMixin, Confirmation, Notification } from '@artup/client'
import AvatarEditor from './avatar-editor.vue'

export default {
  inject: {
    client: { from: 'clientStore' },
    router: { from: 'routerStore' },
    accounts: { from: 'accountsStore' }
  },

  mixins: [
    FormMixin
  ],

  components: {
    'artup-avatar-editor': AvatarEditor
  },

  props: {
    // Identifier of the account to edit, taken from the route if not specified
    id: {
      default: null
    },
    // Indicates that the editor is embedded in another view, such as the My Account tab,
    // rather than opened as a route of its own
    embedded: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // Edited account
      account: null,
      // Indicates that the account is being loaded
      isLoading: true,
      // Indicates that the account is being saved
      isSaving: false,
      // Error which occurred while loading or saving
      error: null,
      // Countries to select from
      countries: Countries.All,
      // Indicates that the avatar editor is open
      isEditingAvatar: false,
      // Avatar being edited, null when adding a new one
      editedAvatar: null
    }
  },

  computed: {
    // Identifier of the edited account
    accountId () {
      return Number(this.id ?? this.$route.params.id)
    },

    // Editing own account
    isOwnAccount () {
      return this.currentAccount?.id === this.accountId
    },

    // Confidential settings - enabled and verified flags, security profiles of avatars -
    // can only be changed by an administrator editing somebody else's account
    canEditConfidential () {
      return this.isAdministrator && !this.isOwnAccount
    },

    // Logged in account
    currentAccount () {
      return this.accounts.account
    },

    // Only administrators may edit accounts
    isAdministrator () {
      return Boolean(this.currentAccount?.isAdministrator)
    },

    // Only super-users may assign the super-user profile to avatars,
    // and never to themselves - another super-user has to do that
    canAssignSuperUser () {
      return Boolean(this.currentAccount?.isSuperAccount) && !this.isOwnAccount
    },

    // Super-user avatars of the edited account
    superUserAvatars () {
      return this.avatars.filter(avatar => avatar.securityProfileName === StandardSecurityProfiles.Super)
    },

    // Security profiles to assign to avatars
    securityProfiles () {
      return this.accounts.securityProfiles
    },

    // Avatars of the edited account
    avatars () {
      return this.account?.avatars || []
    },

    // Default avatar of the edited account, whose first and last name are edited on the account level
    defaultAvatar () {
      return this.account?.avatar
    },

    // Page title
    title () {
      const { account } = this
      return account ? `${account.fullName}` : 'Account'
    }
  },

  methods: {
    // Loads the account and the security profiles
    async populate () {
      this.isLoading = true
      this.error = null

      try {
        const [account] = await Promise.all([
          this.accounts.loadAccount({ id: this.accountId }),
          this.accounts.loadSecurityProfiles()
        ])
        if (!account) throw new Error('Account not found')
        // Work on a copy, so that cancelling leaves the listing untouched
        this.account = new Account(JSON.parse(JSON.stringify(account)))

      } catch (error) {
        Log.exception(error)
        this.error = error

      } finally {
        this.isLoading = false
      }
    },

    // Filter function for filtering options in the country selector
    filterCountries (value = '', update) {
      update(() => {
        if (value === '') {
          this.countries = Countries.All
        } else {
          const text = value.toLowerCase()
          this.countries = Countries.All.filter(o => o.name.toLowerCase().includes(text))
        }
      })
    },

    // Returns the display name of a security profile
    profileLabel (name) {
      const profile = this.securityProfiles.find(profile => profile.name === name)
      return profile?.description || name
    },

    // Opens the editor for a new avatar
    addAvatar () {
      this.editedAvatar = null
      this.isEditingAvatar = true
    },

    // Opens the editor for the specified avatar
    editAvatar (avatar) {
      this.editedAvatar = avatar
      this.isEditingAvatar = true
    },

    // Stores the avatar returned from the editor
    avatarEdited (data) {
      const { avatars, editedAvatar } = this

      if (editedAvatar) {
        Object.assign(editedAvatar, data)
      } else {
        avatars.push(data)
      }

      // Exactly one default avatar: the one just marked wins, otherwise keep what was there
      const changed = editedAvatar || avatars[avatars.length - 1]
      if (changed.isDefault) {
        for (const avatar of avatars) {
          avatar.isDefault = avatar === changed
        }
      } else if (!avatars.some(avatar => avatar.isDefault)) {
        avatars[0].isDefault = true
      }

      this.editedAvatar = null
    },

    // Marks the specified avatar as the default one
    makeDefault (avatar) {
      for (const item of this.avatars) {
        item.isDefault = item === avatar
      }
    },

    // Indicates that the avatar cannot be deleted, with the reason
    deletionBlocked (avatar) {
      const { avatars, isOwnAccount, superUserAvatars } = this
      if (avatars.length <= 1) {
        return 'The last avatar cannot be deleted'
      }
      const isLastSuperUser = superUserAvatars.length === 1 && superUserAvatars[0] === avatar
      if (isOwnAccount && isLastSuperUser) {
        return 'You cannot remove your own last super-user avatar, another super-user has to do that'
      }
      return null
    },

    // Removes the specified avatar, unless blocked
    async deleteAvatar (avatar) {
      const { avatars } = this
      const blocked = this.deletionBlocked(avatar)
      if (blocked) {
        Notification.warning({ message: 'Cannot delete', details: blocked })
        return
      }

      const yes = await Confirmation.ask({
        title: 'Delete avatar',
        message: `Delete avatar ${avatar.name || avatar.fullName || ''}? The change is applied when you save the account.`
      })
      if (!yes) return

      const index = avatars.indexOf(avatar)
      avatars.splice(index, 1)
      if (avatar.isDefault) {
        avatars[0].isDefault = true
      }
    },

    // Saves the account
    async save () {
      if (!await this.validate()) return

      this.isSaving = true
      this.error = null

      try {
        const result = await this.accounts.saveAccount({ account: this.account })
        if (result?.id) {
          Notification.success({ message: 'Account saved', details: `${result.fullName} has been saved.` })
          if (this.embedded) {
            await this.populate()
          } else {
            this.close()
          }
        } else {
          this.error = new Error(result?.message || 'Account could not be saved')
        }

      } catch (error) {
        Log.exception(error)
        this.error = error

      } finally {
        this.isSaving = false
      }
    },

    // Returns to the accounts list, or discards the changes when embedded
    close () {
      if (this.embedded) {
        this.populate()
      } else {
        this.router.gotoRoute({ name: 'accounts-list' })
      }
    }
  },

  async created () {
    // Everyone may edit their own account, other accounts are for administrators only
    if (!(this.isOwnAccount || this.isAdministrator)) {
      this.accounts.gotoNotAuthorized()
      return
    }
    await this.populate()
  }
}
</script>

<template>
  <main class="account-editor" :class="{ embedded }">
    <header class="row items-center q-mb-lg">
      <q-btn v-if="!embedded" icon="arrow_back" flat round dense color="grey-7" class="q-mr-sm" @click="close()">
        <q-tooltip>
          Back to accounts
        </q-tooltip>
      </q-btn>
      <span class="text-h5">
        {{ isOwnAccount ? 'My Account' : title }}
      </span>
    </header>

    <div v-if="isLoading" class="column items-center q-pa-xl">
      <q-spinner-dots color="primary" size="3em">
      </q-spinner-dots>
    </div>

    <div v-else-if="error && !account" class="text-negative">
      {{ error.message }}
    </div>

    <template v-else-if="account">
      <q-form ref="form" class="form">
        <section class="basics">
          <div class="section-title">
            Account
          </div>

          <div class="fields column q-gutter-md">
            <q-input class="col-12 col-md-6" label="E-mail" outlined readonly :model-value="account.name">
            </q-input>

            <template v-if="defaultAvatar">
              <q-input class="col-12 col-md-6" label="First name" outlined v-model="defaultAvatar.firstName" maxlength="255">
              </q-input>
              <q-input class="col-12 col-md-6" label="Last name" outlined v-model="defaultAvatar.lastName" maxlength="255">
              </q-input>
            </template>

            <q-select class="col-12 col-md-6" outlined label="Country" v-model="account.countryCode" :options="countries"
              emit-value map-options option-value="code" option-label="name" use-input fill-input hide-selected
              debounce="300" @filter="filterCountries">
            </q-select>

            <div v-if="canEditConfidential" class="row q-gutter-lg">
              <q-toggle label="Enabled" v-model="account.isEnabled" color="indigo-6">
              </q-toggle>
              <q-toggle label="Verified" v-model="account.isVerified" color="green-7">
              </q-toggle>
            </div>
            <div v-else class="row q-gutter-sm">
              <q-badge :color="account.isEnabled ? 'green-7' : 'red-7'" :label="account.isEnabled ? 'enabled' : 'disabled'">
              </q-badge>
              <q-badge :color="account.isVerified ? 'green-7' : 'orange-7'" :label="account.isVerified ? 'verified' : 'not verified'">
              </q-badge>
            </div>
          </div>
        </section>

        <section class="avatars q-mt-xl">
          <div class="row items-center q-mb-sm">
            <div class="section-title">
              Avatars
            </div>
            <q-space></q-space>
            <q-btn label="Add avatar" icon="add" color="indigo-6" flat no-caps dense @click="addAvatar()">
            </q-btn>
          </div>

          <q-list bordered separator>
            <q-item v-for="avatar in avatars" :key="avatar.id || avatar.name" clickable @click="editAvatar(avatar)">
              <q-item-section avatar>
                <q-icon :name="avatar.isDefault ? 'star' : 'person'" :color="avatar.isDefault ? 'amber-7' : 'grey-6'">
                </q-icon>
              </q-item-section>
              <q-item-section>
                <q-item-label>
                  {{ avatar.fullName || avatar.name }}
                  <q-badge v-if="avatar.isDefault" color="amber-7" label="default" class="q-ml-sm">
                  </q-badge>
                  <q-badge v-if="!avatar.isEnabled" color="red-7" label="disabled" class="q-ml-sm">
                  </q-badge>
                </q-item-label>
                <q-item-label caption>
                  {{ profileLabel(avatar.securityProfileName) }}
                  <span v-if="avatar.organization"> · {{ avatar.organization }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <div class="row q-gutter-xs">
                  <q-btn v-if="!avatar.isDefault" icon="star_outline" flat round dense color="grey-7"
                    @click.stop="makeDefault(avatar)">
                    <q-tooltip>
                      Make default
                    </q-tooltip>
                  </q-btn>
                  <q-btn icon="edit" flat round dense color="grey-7" @click.stop="editAvatar(avatar)">
                    <q-tooltip>
                      Edit
                    </q-tooltip>
                  </q-btn>
                  <q-btn icon="delete" flat round dense color="grey-7" :disable="Boolean(deletionBlocked(avatar))"
                    @click.stop="deleteAvatar(avatar)">
                    <q-tooltip>
                      {{ deletionBlocked(avatar) || 'Delete' }}
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </section>
      </q-form>

      <div v-if="error" class="text-negative q-mt-lg">
        {{ error.message }}
      </div>

      <footer class="row justify-between q-mt-xl">
        <q-btn :label="embedded ? 'Reset' : 'Cancel'" color="grey-6" unelevated no-caps class="button" :disable="isSaving"
          @click="close()">
        </q-btn>
        <q-btn label="Save" color="indigo-6" unelevated no-caps class="button" :loading="isSaving" @click="save()">
        </q-btn>
      </footer>

      <artup-avatar-editor v-model="isEditingAvatar" :avatar="editedAvatar" :security-profiles="securityProfiles"
        :confidential="canEditConfidential" :avatar-count="avatars.length" :super-user="canAssignSuperUser"
        @ok="avatarEdited($event)">
      </artup-avatar-editor>
    </template>
  </main>
</template>

<style scoped lang="scss">
main.account-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 900px;

  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #616161;
    margin-bottom: 12px;
  }

  .fields {
    max-width: 520px;
  }

  .button {
    min-width: 110px;
  }
}
</style>
